import { useCallback, useMemo, useState } from "react";
import {
    addToast,
    Alert,
    Button,
    Chip,
    Divider,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@heroui/react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArchive,
    faArrowUpRightFromSquare,
    faCopy,
    faDownload,
    faFileLines,
    faLink,
    faPlus,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import TableV2 from "@/components/table/TableV2";
import useAuth from "@/lib/auth/useAuth";
import useConfirm from "@/lib/confirm/useConfirm";
import { useI18n } from "@/lib/i18n/useI18n";
import useRequest from "@/lib/request/useRequest";
import { CspResource, CspResourceCsp } from "@/types/cspResource";

function CspResourceTable({
    scope,
    onSendResource,
}: {
    scope: CspResourceCsp | "all";
    onSendResource?: () => void;
}) {
    const { userIsSentinel } = useAuth();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { showConfirm } = useConfirm();
    const { formatDateMMDDYYYY } = useI18n();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState("");
    const [sorting, setSorting] = useState<SortingState>([]);
    const canManage = scope === "all" && userIsSentinel;
    const organizationId = scope === "all" ? undefined : String(scope.id);
    const resources = useGetRequest<CspResource[]>({
        url: "/csp_resources/",
        params: organizationId ? { organization: organizationId } : {},
        enabled: scope !== "all" || userIsSentinel,
    });
    const {
        mutate: downloadFile,
        isPending: downloadPending,
        variables: downloadVariables,
    } = useMutateRequest<Blob>({
        errorMessage: "Unable to download this resource. Please try again.",
    });
    const {
        mutateAsync: archiveResource,
        isPending: archivePending,
        variables: archiveVariables,
    } = useMutateRequest({
        successMessage: "Resource archived",
        errorMessage: "Unable to archive this resource. Please try again.",
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/csp_resources/"] });
            queryClient.invalidateQueries({
                queryKey: ["/csp_resources/recipients/"],
            });
        },
    });
    const visibleResources = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (resources.data ?? []).filter((resource) =>
            `${resource.title} ${resource.description}`
                .toLowerCase()
                .includes(term),
        );
    }, [resources.data, search]);

    const downloadResource = useCallback(
        (resource: CspResource) => {
            downloadFile(
                {
                    url: `/csp_resources/${resource.id}/download/`,
                    method: "get",
                    params: organizationId
                        ? { organization: organizationId }
                        : {},
                    responseType: "blob",
                },
                {
                    onSuccess: (blob) => {
                        const url = window.URL.createObjectURL(blob);
                        const anchor = document.createElement("a");
                        anchor.href = url;
                        anchor.download = resource.file_name;
                        document.body.appendChild(anchor);
                        anchor.click();
                        anchor.remove();
                        window.URL.revokeObjectURL(url);
                    },
                },
            );
        },
        [downloadFile, organizationId],
    );
    const copyResourceLink = useCallback(async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            addToast({
                title: "Link copied",
                color: "success",
                timeout: 3000,
            });
        } catch {
            addToast({
                title: "Unable to copy link. Please try again.",
                color: "danger",
                timeout: 3000,
            });
        }
    }, []);
    const confirmArchive = useCallback(
        (resource: CspResource) => {
            showConfirm({
                title: "Archive resource?",
                description: `“${resource.title}” will no longer appear for CSPs. Its record and file will be kept.`,
                color: "success",
                onConfirm: async () => {
                    await archiveResource({
                        url: `/csp_resources/${resource.id}/archive/`,
                        method: "post",
                    });
                },
            });
        },
        [archiveResource, showConfirm],
    );

    const downloadingUrl = downloadVariables?.url;
    const archivingUrl = archiveVariables?.url;
    const columns = useMemo<ColumnDef<CspResource>[]>(
        () => [
            {
                accessorKey: "title",
                header: "Resource",
                size: 400,
                cell: ({ row }) => (
                    <div className="flex min-w-0 w-full items-start gap-3">
                        <FontAwesomeIcon
                            icon={
                                row.original.source === "file"
                                    ? faFileLines
                                    : faLink
                            }
                            className="mt-1 shrink-0 text-primary-500"
                        />
                        <div className="min-w-0 flex-1">
                            <span
                                className="block truncate font-semibold"
                                title={row.original.title}
                            >
                                {row.original.title}
                            </span>
                            {row.original.description && (
                                <p
                                    className="truncate text-xs text-gray-500"
                                    title={row.original.description}
                                >
                                    {row.original.description}
                                </p>
                            )}
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: "source",
                header: "Type",
                size: 100,
                cell: ({ row }) => (
                    <Chip size="sm" variant="flat">
                        {row.original.source === "file" ? "File" : "Link"}
                    </Chip>
                ),
            },
            {
                id: "sent_to",
                accessorFn: (row) =>
                    row.sent_to
                        ?.map((group) =>
                            [group.label, ...group.csps.map((csp) => csp.name)]
                                .filter(Boolean)
                                .join(", "),
                        )
                        .join("; ") ?? "",
                header: "Recipient(s)",
                size: 240,
                cell: ({ row }) => {
                    const groups = row.original.sent_to;
                    if (!groups?.length) return "—";
                    const first = groups[0];
                    const csp = first.csps[0];
                    const hasMore = groups.length > 1 || first.csps.length > 1;

                    return (
                        <div className="flex min-w-0 w-full items-center gap-2">
                            {first.label && (
                                <span
                                    className="min-w-0 truncate"
                                    title={first.label}
                                >
                                    {first.label}
                                </span>
                            )}
                            {csp && (
                                <Link
                                    to="/dashboard/resources/csp-view"
                                    search={{ organization: String(csp.id) }}
                                    onClick={(event) => event.stopPropagation()}
                                    className="min-w-0 truncate font-medium text-primary-500 hover:underline"
                                    title={csp.name}
                                >
                                    {csp.name}
                                </Link>
                            )}
                            {hasMore && (
                                <Popover placement="bottom-start">
                                    <PopoverTrigger>
                                        <Button
                                            size="sm"
                                            variant="flat"
                                            color="primary"
                                            className="h-6 min-w-0 shrink-0 px-2 text-xs"
                                            aria-label={`View all recipients for ${row.original.title}`}
                                        >
                                            View all
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-80 max-w-[calc(100vw-2rem)] items-start p-3"
                                        aria-label={`Recipients for ${row.original.title}`}
                                    >
                                        <div
                                            className="flex max-h-64 w-full flex-col gap-2 overflow-y-auto"
                                            tabIndex={0}
                                            role="region"
                                            aria-label={`Recipients for ${row.original.title}`}
                                        >
                                            {groups.map((group) => (
                                                <div key={group.label}>
                                                    {group.label && (
                                                        <p className="text-sm font-medium">
                                                            {group.label}
                                                        </p>
                                                    )}
                                                    {group.csps.map((csp) => (
                                                        <Link
                                                            key={csp.id}
                                                            to="/dashboard/resources/csp-view"
                                                            search={{
                                                                organization:
                                                                    String(
                                                                        csp.id,
                                                                    ),
                                                            }}
                                                            onClick={(event) =>
                                                                event.stopPropagation()
                                                            }
                                                            className="block break-words text-sm text-primary-500 hover:underline"
                                                        >
                                                            {csp.name}
                                                        </Link>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                    );
                },
            },
            {
                accessorKey: "created_date",
                header: "Added",
                size: 120,
                cell: ({ row }) =>
                    row.original.created_date
                        ? formatDateMMDDYYYY(row.original.created_date)
                        : "—",
            },
            {
                id: "actions",
                header: "Actions",
                size: canManage ? 360 : 250,
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        {row.original.source === "link" ? (
                            <>
                                <Button
                                    as="a"
                                    href={row.original.external_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="sm"
                                    variant="flat"
                                    color="primary"
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                        />
                                    }
                                    aria-label={`Open ${row.original.title} in a new tab`}
                                >
                                    Open
                                </Button>
                                <Button
                                    size="sm"
                                    variant="flat"
                                    color="primary"
                                    startContent={
                                        <FontAwesomeIcon icon={faCopy} />
                                    }
                                    onPress={() =>
                                        copyResourceLink(
                                            row.original.external_url,
                                        )
                                    }
                                    aria-label={`Copy link to ${row.original.title}`}
                                >
                                    Copy link
                                </Button>
                            </>
                        ) : (
                            <Button
                                size="sm"
                                variant="flat"
                                color="primary"
                                isDisabled={downloadPending}
                                isLoading={
                                    downloadPending &&
                                    downloadingUrl ===
                                        `/csp_resources/${row.original.id}/download/`
                                }
                                onPress={() => downloadResource(row.original)}
                                startContent={
                                    <FontAwesomeIcon icon={faDownload} />
                                }
                                aria-label={`Download ${row.original.title}`}
                            >
                                Download
                            </Button>
                        )}
                        {canManage && (
                            <Button
                                size="sm"
                                variant="flat"
                                color="danger"
                                isDisabled={archivePending}
                                isLoading={
                                    archivePending &&
                                    archivingUrl ===
                                        `/csp_resources/${row.original.id}/archive/`
                                }
                                onPress={() => confirmArchive(row.original)}
                                startContent={
                                    <FontAwesomeIcon icon={faArchive} />
                                }
                                aria-label={`Archive ${row.original.title}`}
                            >
                                Archive
                            </Button>
                        )}
                    </div>
                ),
            },
        ],
        [
            archivePending,
            archivingUrl,
            canManage,
            confirmArchive,
            copyResourceLink,
            downloadPending,
            downloadingUrl,
            downloadResource,
            formatDateMMDDYYYY,
        ],
    );

    if (scope === "all" && !userIsSentinel) return null;

    return (
        <div className="h-full flex flex-col overflow-hidden bg-white">
            <div
                className={
                    canManage
                        ? "flex w-full flex-wrap items-center justify-between gap-2 p-3"
                        : "flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
                }
            >
                {scope !== "all" && (
                    <div>
                        <h2 className="text-xl font-bold text-primary-500">
                            CSP Resources
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Files and links shared with {scope.name}.
                        </p>
                    </div>
                )}
                <Input
                    aria-label="Search resources"
                    placeholder="Search resources…"
                    value={search}
                    onValueChange={setSearch}
                    isClearable
                    onClear={() => setSearch("")}
                    startContent={<FontAwesomeIcon icon={faSearch} />}
                    className={
                        canManage
                            ? "min-w-0 flex-1 basis-48 max-w-md"
                            : "w-full sm:max-w-md"
                    }
                    size="md"
                />
                {canManage && onSendResource && (
                    <Button
                        color="primary"
                        className="shrink-0"
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                        onPress={onSendResource}
                    >
                        Send resource
                    </Button>
                )}
            </div>
            <Divider />
            <div className="flex-1 min-h-0 p-3">
                {resources.isError ? (
                    <Alert
                        color="danger"
                        title="We couldn’t load your resources."
                        endContent={
                            <Button
                                size="sm"
                                variant="flat"
                                color="primary"
                                isLoading={resources.isFetching}
                                onPress={() => resources.refetch()}
                            >
                                Try again
                            </Button>
                        }
                    />
                ) : (
                    <TableV2
                        data={visibleResources}
                        columns={columns}
                        columnVisibility={{ sent_to: canManage }}
                        sorting={sorting}
                        setSorting={setSorting}
                        isLoading={resources.isPending}
                        estimateSize={60}
                        fillWidthIfPossible
                        showScrollbar
                    />
                )}
            </div>
        </div>
    );
}

export default CspResourceTable;
